'use client';
import React from 'react';
import Image from 'next/image';
import dynamic from 'next/dynamic';
import { useTranslation } from '@repo/ui/src/context/Translation/useTranslation';
import FooterColumn, { ColumnProps } from '@repo/ui/src/components/FooterColumn';
import MarketingCaptureForm from '@repo/ui/src/components/MarketingCapture/_MarketingCaptureForm';
import { Anchor } from '@repo/ui/src/components/Anchor';
import { MarketingSignupProps } from '@repo/ui/src/types/marketingSignup';
import { useEmailSignup } from '@src/hooks/useEmailSignup';
import SocialLinks from '@repo/ui/src/components/SocialLinks';
import MeaterLanguageSelector from '@src/components/LanguageSelector';
import { classNames } from '@repo/ui/src/utils/classNames';

const Markdown = dynamic(() => import('@repo/ui/src/components/Markdown'));

export interface FooterProps {
  columns?: Array<ColumnProps>;
  emailSignup?: MarketingSignupProps;
  submit?: string;
  bottomRowTitle?: string;
  consentManagerText?: string;
  disclaimer?: string;
}

const Footer = ({
  columns = [],
  emailSignup,
  submit,
  bottomRowTitle,
  consentManagerText,
  disclaimer,
}: FooterProps) => {
  const { t } = useTranslation();
  const submitText = submit ?? t('footer.emailSignup.submit', undefined, { default: 'Sign Up' });
  const doNotSellText = t('cookieConsentManager.doNotSellText', undefined, {
    default: 'Your Privacy Choices',
  });

  return (
    <footer className="bg-neutral-900 pt-12 text-neutral-300">
      <div className="max-w-[1440px] mx-auto px-8 pb-12">
        <hr className="border-neutral-400 mx-auto max-w-[1440px] px-8" />
        <div className="pt-12 flex flex-col lg:flex-row gap-10 lg:gap-0 justify-between">
          <div className="flex-1 max-w-80 flex flex-col gap-4 shrink-0 lg:w-48">
            <Image
              src="/assets/images/meater-logo.svg"
              alt="MEATER"
              width={100}
              height={46}
              priority={false}
            />
            <p>{t('footer.tagline')}</p>
          </div>

          <div>
            <FooterColumn columns={columns} />
          </div>

          {emailSignup && (
            <div className="flex-1 pb-6 grid flex-col gap-5 max-w-[370px]">
              <MarketingCaptureForm
                emailSignup={emailSignup}
                submitButton={submitText}
                success={emailSignup.success}
                failure={emailSignup.failure}
                sourceDetail="footer"
                darkMode
                fullWidth
                useEmailSignup={useEmailSignup}
              />
              {disclaimer && (
                <Markdown
                  className={classNames(
                    'text-neutral-200 tra-text-web-body-s hover:[&>a]:text-brand-200',
                  )}
                >
                  {disclaimer}
                </Markdown>
              )}
            </div>
          )}
        </div>

        <div className="flex gap-8 justify-between mt-6 flex-col md:flex-row">
          <p className="tra-text-web-body-s max-w-[760px]">
            {t('legal.bluetoothTrademark', undefined, {
              default:
                'The Bluetooth® word mark and logos are registered trademarks owned by Bluetooth SIG, Inc. and any use of such marks by Apption Labs is under license. Other trademarks and trade names are those of their respective owners.',
            })}
          </p>
          <SocialLinks
            instagram="meatermade"
            facebook="MEATERmade"
            tiktok="meatermade"
            youtube="c/meater"
            pinterest="meatermade"
            twitter="MEATERmade"
          />
        </div>
      </div>

      <div className="bg-neutral-800">
        <div className="max-w-[1440px] mx-auto px-8 py-4 flex flex-col sm:flex-row justify-center items-start sm:items-center gap-3">
          <MeaterLanguageSelector showLabel className="flex-1" />

          <span className="tra-text-web-body-s">{bottomRowTitle}</span>

          {(consentManagerText || doNotSellText !== 'none') && (
            <span className="tra-text-web-body-s shrink-0 flex-1 flex justify-end">
              <div className="flex flex-col content-start gap-1">
                {consentManagerText && (
                  <button
                    onClick={() => window.DG_BANNER_API?.showConsentBanner()}
                    className="hover:text-brand-300 hover:underline text-left"
                  >
                    {consentManagerText}
                  </button>
                )}
                {doNotSellText !== 'none' && (
                  <Anchor
                    externalLink="https://preferences.traeger.com/?multibrand-group=meater"
                    className="hover:text-brand-300 hover:underline"
                    isExternal
                  >
                    <Image
                      src="/assets/images/privacy.png"
                      alt="Privacy"
                      width={30}
                      height={18}
                      className="inline-block mr-2"
                    />
                    {doNotSellText}
                  </Anchor>
                )}
              </div>
            </span>
          )}
        </div>
      </div>
    </footer>
  );
};

export default Footer;
